import { useState, useContext, useCallback, useEffect, useRef } from 'react';
import { AuthContext } from '../../providers/AuthProvider';
import { CsrfContext } from '../../../../providers/CsrfProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import { UseSetupMFAForm } from '../steps/Hooks/UseSetupMFAForm';
import { UseVerifyMFAForm } from '../steps/Hooks/UseVerifyMFAForm';
import { triggerResetPassToken, sendResetPass, signup, completeSignup, confirmEmail } from '../../../../services/Users';
import { UseAuthForm, AuthFormData } from "./UseAuthForm";

export type StepState = 'enabled' | 'disabled' | 'hidden';

export type StepName = 'login' | 'signup' | 'email' | 'verifyEmail' | 'newPassword' | 'passwordConfirmation' | 'setupMFA' | 'verifyMFA';

export type FlowType = 'login' | 'forgotPassword' | 'signup' | 'confirm';

export interface StepConfig {
    login?: StepState;
    signup?: StepState;
    email?: StepState;
    verifyEmail?: StepState;
    newPassword?: StepState;
    passwordConfirmation?: StepState;
    setupMFA?: StepState;
    verifyMFA?: StepState;
}

export interface UseAuthFlowManagerOptions {
    onStepTransition?: (step: StepName, error: string | null, mfaData?: any) => void;
    initialStepConfig?: StepConfig;
    flowType?: FlowType;
    urlCode?: string | null;
    urlEmail?: string | null;
    prefillEmail?: string;
    onEmailChange?: (email: string) => void;
}

export interface StepData {
    formData: AuthFormData | { mfaData?: any; email?: string; mfaVerificationData?: any };
    onChangeAuthForm: (e: React.ChangeEvent<HTMLInputElement>) => void;
    validatePasswordMatch: () => boolean;
    validateNewPasswordMatch: () => boolean;
    validateOTPMatch: () => boolean;
    email?: string;
    password?: string;
}

export interface UseAuthFlowManagerReturn {
    // Core state
    currentStep: StepName;
    loginError: string | null;
    isLoading: boolean;
    mfaData: any;
    verificationSubmitted: boolean;
    passwordSubmitted: boolean;
    stepConfig: StepConfig;
    csrfToken: string | null;

    // Step state setters
    setStateLogin: (state: StepState) => void;
    setStateSignup: (state: StepState) => void;
    setStateEmail: (state: StepState) => void;
    setStateEmailVerify: (state: StepState) => void;
    setStatePasswordConfirmation: (state: StepState) => void;
    setStateNewPassword: (state: StepState) => void;
    setStateMFASetup: (state: StepState) => void;
    setStateMFAVerify: (state: StepState) => void;

    // Step configuration helpers
    isStepVisible: (stepName: string) => boolean;
    isStepEnabled: (stepName: string) => boolean;
    isStepDisabled: (stepName: string) => boolean;
    disableLastStep: () => void;

    // Flow control
    handleStepTransition: (newStep: StepName, error?: string | null, mfaDataParam?: any) => void;
    handleLoginSuccess: (claims: any) => void;
    handleLoginError: (error: string) => void;

    // Forgot password handlers
    handleEmailSubmit: () => Promise<void>;
    handleVerificationSubmit: () => void;
    handlePasswordSubmit: () => Promise<void>;
    handleResendCode: () => Promise<void>;

    // Signup handlers
    handleSignupInfoSubmit: () => Promise<void>;
    handleSignupSubmit: () => Promise<void>;

    // Confirm handlers
    handleConfirmEmailSubmit: () => Promise<void>;

    // Reset functions
    resetToLogin: () => void;
    resetAllSteps: () => void;

    // Utility functions
    getCurrentStepConfig: () => StepConfig;
    updateStepConfig: (newStepConfig: StepConfig) => void;
    getStepData: (stepName: StepName) => StepData | null;

    // Loading state control
    setIsLoading: (loading: boolean) => void;

    // Form data access
    authFormData: AuthFormData;
    onChangeAuthForm: (e: React.ChangeEvent<HTMLInputElement>) => void;
    resetAuthForm: () => void;
    handleLogin: (email: string, password: string, callbacks?: any) => Promise<void>;
    setupMFAForm: any;
    verifyMFAForm: any;
}

/**
 * useAuthFlowManager - Custom hook for managing authentication flow state
 */
export const useAuthFlowManager = ({
    onStepTransition = () => {},
    initialStepConfig = {
        login: 'enabled',
        email: 'hidden',
        verifyEmail: 'hidden',
        newPassword: 'hidden',
        setupMFA: 'hidden',
        verifyMFA: 'hidden'
    },
    flowType = 'login',
    urlCode,
    urlEmail,
    prefillEmail,
    onEmailChange
}: UseAuthFlowManagerOptions = {}): UseAuthFlowManagerReturn => {
    const { setAuthClaims } = useContext(AuthContext)!;
    const { csrfToken } = useContext(CsrfContext) as unknown as { csrfToken: string | null; setCsrfToken: (token: string) => void };
    const navigate = useNavigate();

    const {
        authFormData,
        onChangeAuthForm: originalOnChangeAuthForm,
        resetAuthForm,
        handleLogin,
        validatePasswordMatch,
        validateNewPasswordMatch,
        validateOTPMatch
    } = UseAuthForm(setAuthClaims, csrfToken, navigate, flowType);

    // Wrap onChangeAuthForm to update shared email state
    const onChangeAuthForm = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        originalOnChangeAuthForm(e);
        // If email field changed, update shared email state
        if (e.target.name === 'email' && onEmailChange) {
            onEmailChange(e.target.value);
        }
    }, [originalOnChangeAuthForm, onEmailChange]);

    // Initialize mini form hooks (keeping for MFA forms that still need them)
    const setupMFAForm = UseSetupMFAForm();
    const verifyMFAForm = UseVerifyMFAForm();

    // Core flow state
    const [currentStep, setCurrentStep] = useState<StepName>(
        flowType === 'forgotPassword' ? 'email' :
        flowType === 'signup' ? 'signup' :
        flowType === 'confirm' ? 'verifyEmail' :
        'login'
    );
    const [loginError, setLoginError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [mfaData, setMfaData] = useState<any>(null);
    const [verificationSubmitted, setVerificationSubmitted] = useState<boolean>(false);
    const [passwordSubmitted, setPasswordSubmitted] = useState<boolean>(false);

    // Step configuration state - use provided initialStepConfig or defaults
    const [stepConfig, setStepConfig] = useState<StepConfig>(initialStepConfig);

    // Track if we've already populated from URL to prevent overwriting user edits
    const hasPopulatedFromUrl = useRef(false);
    const initialUrlCode = useRef<string | null>(null);
    const initialUrlEmail = useRef<string | null>(null);

    // Handle URL params for confirm flow - only populate once on initial mount
    useEffect(() => {
        if (flowType === 'confirm' && !hasPopulatedFromUrl.current) {
            // Store initial URL values
            if (urlCode && urlCode.trim() !== '' && urlCode.length === 6) {
                initialUrlCode.current = urlCode;
            }
            if (urlEmail && urlEmail.trim() !== '') {
                initialUrlEmail.current = urlEmail;
            }

            // Pre-populate form with URL params if they exist
            // Only do this once on initial mount to avoid overwriting user edits
            if (initialUrlEmail.current && initialUrlEmail.current.trim() !== '') {
                try {
                    const decodedEmail = decodeURIComponent(initialUrlEmail.current);
                    onChangeAuthForm({
                        target: { name: 'email', value: decodedEmail }
                    } as React.ChangeEvent<HTMLInputElement>);
                } catch (e) {
                    // If decodeURIComponent fails, use the original value
                    onChangeAuthForm({
                        target: { name: 'email', value: initialUrlEmail.current }
                    } as React.ChangeEvent<HTMLInputElement>);
                }
            }
            if (initialUrlCode.current && initialUrlCode.current.trim() !== '' && initialUrlCode.current.length === 6) {
                onChangeAuthForm({
                    target: { name: 'verificationCode', value: initialUrlCode.current }
                } as React.ChangeEvent<HTMLInputElement>);
            }
            
            // Mark as populated so we never do this again
            hasPopulatedFromUrl.current = true;
        }
    }, [flowType, urlCode, urlEmail, onChangeAuthForm]);

    // Handle prefillEmail for signup, login, and forgotPassword flows
    // Update email when prefillEmail changes (e.g., when switching tabs with updated email)
    useEffect(() => {
        if (prefillEmail !== undefined) {
            if (flowType === 'signup' || flowType === 'login' || flowType === 'forgotPassword') {
                // Only update if the email is different from current value
                // This allows the shared email to update when switching tabs
                const currentEmail = authFormData.email || '';
                const newEmail = prefillEmail.trim();
                if (currentEmail !== newEmail) {
                    onChangeAuthForm({
                        target: { name: 'email', value: newEmail }
                    } as React.ChangeEvent<HTMLInputElement>);
                }
            }
        }
    }, [flowType, prefillEmail, authFormData.email, onChangeAuthForm]);

    // Step state management functions
    const setStateLogin = useCallback((state: StepState) => {
        setStepConfig(prev => ({ ...prev, login: state }));
    }, []);

    const setStateSignup = useCallback((state: StepState) => {
        setStepConfig(prev => ({ ...prev, signup: state }));
    }, []);

    const setStateEmail = useCallback((state: StepState) => {
        setStepConfig(prev => ({ ...prev, email: state }));
    }, []);

    const setStateEmailVerify = useCallback((state: StepState) => {
        setStepConfig(prev => ({ ...prev, verifyEmail: state }));
    }, []);

    const setStatePasswordConfirmation = useCallback((state: StepState) => {
        setStepConfig(prev => ({ ...prev, passwordConfirmation: state }));
    }, []);

    const setStateNewPassword = useCallback((state: StepState) => {
        setStepConfig(prev => ({ ...prev, newPassword: state }));
    }, []);

    const setStateMFASetup = useCallback((state: StepState) => {
        setStepConfig(prev => ({ ...prev, setupMFA: state }));
    }, []);

    const setStateMFAVerify = useCallback((state: StepState) => {
        setStepConfig(prev => ({ ...prev, verifyMFA: state }));
    }, []);

    // Helper functions for step configuration
    const isStepVisible = useCallback((stepName: string): boolean => {
        return stepConfig[stepName as keyof StepConfig] !== undefined &&
               (stepConfig[stepName as keyof StepConfig] === 'enabled' || stepConfig[stepName as keyof StepConfig] === 'disabled');
    }, [stepConfig]);

    const isStepEnabled = useCallback((stepName: string): boolean => {
        return stepConfig[stepName as keyof StepConfig] === 'enabled';
    }, [stepConfig]);

    const isStepDisabled = useCallback((stepName: string): boolean => {
        return stepConfig[stepName as keyof StepConfig] === 'disabled';
    }, [stepConfig]);

    const disableLastStep = useCallback(() => {
        setStepConfig(prev => {
            const newConfig = { ...prev };
            // Find the currently enabled step and disable it
            Object.keys(newConfig).forEach(step => {
                if (newConfig[step as keyof StepConfig] === 'enabled') {
                    (newConfig as any)[step] = 'disabled';
                }
            });
            return newConfig;
        });
    }, []);

    // Step transition handler
    const handleStepTransition = useCallback((newStep: StepName, error: string | null = null, mfaDataParam: any = null) => {
        setCurrentStep(newStep);
        setLoginError(error);
        setIsLoading(false);

        // First disable all currently enabled steps, then enable the new step
        setStepConfig(prev => {
            const newConfig = { ...prev };

            // Disable all currently enabled steps
            Object.keys(newConfig).forEach(step => {
                if (newConfig[step as keyof StepConfig] === 'enabled') {
                    (newConfig as any)[step] = 'disabled';
                }
            });

            // Enable the new step
            (newConfig as any)[newStep] = 'enabled';
            return newConfig;
        });

        // Handle MFA data if provided
        if (mfaDataParam) {
            setMfaData(mfaDataParam);
            // Update the appropriate form data if it's a complete formData object
            if (mfaDataParam.mfaSetupComplete !== undefined) {
                setupMFAForm.setMFASetupComplete(mfaDataParam.mfaSetupComplete);
            }
            if (mfaDataParam.mfaVerifyData) {
                (setupMFAForm.formData as any).mfaVerifyData = mfaDataParam.mfaVerifyData;
            }
        }

        // Call the external onStepTransition callback - forms handle their own state updates
        onStepTransition(newStep, error, mfaDataParam);
    }, [onStepTransition, setupMFAForm]);

    // Success and error handlers
    const handleLoginSuccess = useCallback((claims: any) => {
        if (claims && claims.user_sub) {
            (window as any).__lastLoginTime = Date.now();
            setAuthClaims(claims);
            setTimeout(() => {
                navigate('/dashboard');
            }, 100);
        }
    }, [setAuthClaims, navigate]);

    const handleLoginError = useCallback((error: string) => {
        setLoginError(error);
        setIsLoading(false);
    }, []);

    // Forgot password specific handlers
    const handleEmailSubmit = useCallback(async () => {
        setLoginError(null);
        if (!authFormData.isValid) {
            setLoginError("Please enter a valid email address");
            return;
        }

        setIsLoading(true);
        try {
            await triggerResetPassToken(authFormData.email, csrfToken);
            handleStepTransition('newPassword');
        } catch (err) {
            console.error('Reset email failed:', err);
            setLoginError("Failed to send reset email. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [authFormData.isValid, authFormData.email, csrfToken, handleStepTransition]);

    const handleVerificationSubmit = useCallback(() => {
        setLoginError(null);
        if (!authFormData.isValid) {
            setLoginError("Please enter a complete 6-digit verification code");
            return;
        }

        setVerificationSubmitted(true);
        // Transition to different steps based on flow type
        if (flowType === 'signup') {
            handleStepTransition('passwordConfirmation');
        } else {
            handleStepTransition('newPassword');
        }
    }, [authFormData.isValid, flowType, handleStepTransition]);

    const handlePasswordSubmit = useCallback(async () => {
        setLoginError(null);
        if (!authFormData.isValid) {
            setLoginError("Please enter a valid password and confirmation");
            return;
        }

        // Verify that the new password confirmation matches the original new password
        if (authFormData.confirmNewPassword && authFormData.confirmNewPassword !== authFormData.newPassword) {
            setLoginError("New passwords do not match");
            return;
        }

        setIsLoading(true);
        try {
            const response = await sendResetPass(
                authFormData.email,
                authFormData.newPassword,
                authFormData.verificationCode,
                csrfToken
            );

            if (response.data.error) {
                setLoginError(response.data.error);
                return;
            }

            // Check if MFA setup is required
            if (response.data.requires_mfa_setup) {
                handleStepTransition('setupMFA', null, response.data.mfa_data);
            } else if (response.data.requires_mfa_verification) {
                handleStepTransition('verifyMFA', null, response.data.mfa_data);
            } else {
                // Password reset complete, automatically log the user in
                try {
                    await handleLogin(
                        authFormData.email,
                        authFormData.newPassword,
                        {
                            onMfaSetup: (mfaData: any) => handleStepTransition('setupMFA', null, mfaData),
                            onMfaVerify: (mfaData: any) => handleStepTransition('verifyMFA', null, mfaData),
                            onError: (error: string) => setLoginError(error),
                            onSuccess: (claims: any) => {
                                setPasswordSubmitted(true);
                                navigate('/dashboard');
                            }
                        }
                    );
                } catch (loginErr) {
                    // If auto-login fails, redirect to login page
                    console.error('Auto-login failed:', loginErr);
                    navigate('/login');
                }
            }
        } catch (err) {
            setLoginError("Password reset failed. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [authFormData.isValid, authFormData.email, authFormData.verificationCode, authFormData.newPassword, csrfToken, handleStepTransition, history, handleLogin]);

    const handleResendCode = useCallback(async () => {
        setIsLoading(true);
        try {
            await triggerResetPassToken(authFormData.email, csrfToken);
        } catch (err) {
            setLoginError("Failed to resend reset email. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [authFormData.email, csrfToken]);

    // Signup specific handlers
    const handleSignupInfoSubmit = useCallback(async () => {
        setLoginError(null);
        if (!authFormData.isValid) {
            setLoginError("Please enter a valid name, email address, and password");
            return;
        }

        setIsLoading(true);
        try {
            // Send email verification request for signup
            const response = await signup(
                authFormData.email,
                authFormData.name || 'User',
                authFormData.password,
                csrfToken
            );

            if (response.data.error) {
                setLoginError(response.data.error);
            } else {
                // Email verification sent successfully, transition to verifyEmail step
                handleStepTransition('verifyEmail');
            }
        } catch (err) {
            console.error('signup error:', err);
            setLoginError("Signup failed, do you already have an account?.");
        } finally {
            setIsLoading(false);
        }
    }, [authFormData.isValid, authFormData.email, authFormData.name, authFormData.password, csrfToken, handleStepTransition]);

    const handleSignupSubmit = useCallback(async () => {
        setLoginError(null);
        if (!authFormData.isValid) {
            setLoginError("Please enter a valid password and confirmation");
            return;
        }

        // Verify that the password confirmation matches the original password
        if (authFormData.confirmPassword && authFormData.confirmPassword !== authFormData.password) {
            setLoginError("Passwords do not match");
            return;
        }

        setIsLoading(true);
        try {
            // Since email verification has already been completed, proceed with signup
            const response = await completeSignup(
                authFormData.name || '',
                authFormData.email,
                authFormData.password,
                authFormData.verificationCode,
                csrfToken
            );

            if (response.data.error) {
                setLoginError(response.data.error);
                return;
            }

            // Check if MFA setup is required
            if (response.data.requires_mfa_setup) {
                handleStepTransition('setupMFA', null, response.data.mfa_data);
            } else if (response.data.requires_mfa_verification) {
                handleStepTransition('verifyMFA', null, response.data.mfa_data);
            } else {
                // Signup complete, automatically log the user in
                try {
                    await handleLogin(
                        authFormData.email,
                        authFormData.password,
                        {
                            onMfaSetup: (mfaData: any) => handleStepTransition('setupMFA', null, mfaData),
                            onMfaVerify: (mfaData: any) => handleStepTransition('verifyMFA', null, mfaData),
                            onError: (error: string) => setLoginError(error),
                            onSuccess: (claims: any) => {
                                setPasswordSubmitted(true);
                                handleLoginSuccess({ ...claims, email: authFormData.email });
                            }
                        }
                    );
                } catch (loginErr) {
                    setLoginError("Signup successful but login failed. Please try logging in manually.");
                }
            }
        } catch (err: any) {
            setLoginError(err.response?.data?.error || "Signup failed. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [authFormData.email, authFormData.name, authFormData.password, authFormData.confirmPassword, authFormData.isValid, authFormData.verificationCode, handleStepTransition, handleLoginSuccess, csrfToken, handleLogin]);

    // Confirm email handler
    const handleConfirmEmailSubmit = useCallback(async () => {
        setLoginError(null);
        if (!authFormData.email || !authFormData.verificationCode || authFormData.verificationCode.length !== 6) {
            setLoginError("Please enter a valid email and complete 6-digit verification code");
            return;
        }

        setIsLoading(true);
        try {
            const response = await confirmEmail(
                authFormData.email,
                authFormData.verificationCode,
                csrfToken
            );

            if (response.data.error) {
                setLoginError(response.data.error || "Invalid verification code. Please try again.");
                return;
            }

            // Email confirmed successfully, disable verifyEmail step and transition to login step
            // This keeps us in the confirm flow but shows login beneath the deactivated verify step
            setStateEmailVerify('disabled');
            handleStepTransition('login');
        } catch (err: any) {
            console.error('Email confirmation error:', err);
            setLoginError(err.response?.data?.error || "Email confirmation failed. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [authFormData.email, authFormData.verificationCode, csrfToken, setStateEmailVerify, handleStepTransition]);

    // Reset functions
    const resetToLogin = useCallback(() => {
        setCurrentStep('login');
        setLoginError(null);
        setMfaData(null);
        setStepConfig(initialStepConfig);
    }, [initialStepConfig]);

    const resetAllSteps = useCallback(() => {
        setCurrentStep('login');
        setLoginError(null);
        setMfaData(null);
        setIsLoading(false);
        setStepConfig(initialStepConfig);
    }, [initialStepConfig]);

    // Utility functions
    const getCurrentStepConfig = useCallback((): StepConfig => {
        return stepConfig;
    }, [stepConfig]);

    const updateStepConfig = useCallback((newStepConfig: StepConfig) => {
        setStepConfig(newStepConfig);
    }, []);

    // Get step data for forms
    const getStepData = useCallback((stepName: StepName): StepData | null => {
        switch (stepName) {
            case 'login':
                return {
                    formData: authFormData,
                    onChangeAuthForm: onChangeAuthForm,
                    validatePasswordMatch,
                    validateNewPasswordMatch,
                    validateOTPMatch
                };
            case 'signup':
                return {
                    formData: authFormData,
                    onChangeAuthForm: onChangeAuthForm,
                    validatePasswordMatch,
                    validateNewPasswordMatch,
                    validateOTPMatch
                };
            case 'email':
                return {
                    formData: authFormData,
                    onChangeAuthForm: onChangeAuthForm,
                    validatePasswordMatch,
                    validateNewPasswordMatch,
                    validateOTPMatch
                };
            case 'verifyEmail':
                return {
                    formData: authFormData,
                    onChangeAuthForm: onChangeAuthForm,
                    validatePasswordMatch,
                    validateNewPasswordMatch,
                    validateOTPMatch
                };
            case 'newPassword':
                return {
                    formData: authFormData,
                    onChangeAuthForm: onChangeAuthForm,
                    validatePasswordMatch,
                    validateNewPasswordMatch,
                    validateOTPMatch
                };
            case 'passwordConfirmation':
                return {
                    formData: authFormData,
                    onChangeAuthForm: onChangeAuthForm,
                    validatePasswordMatch,
                    validateNewPasswordMatch,
                    validateOTPMatch
                };
            case 'setupMFA':
                return {
                    formData: { mfaData: mfaData },
                    onChangeAuthForm: onChangeAuthForm,
                    email: authFormData.email,
                    password: authFormData.password,
                    validatePasswordMatch,
                    validateNewPasswordMatch,
                    validateOTPMatch
                };
            case 'verifyMFA':
                // Handle different possible MFA data structures
                let mfaVerificationData = null;
                if (mfaData) {
                    // Check if it's already in the expected format
                    if (mfaData.mfa_session_id && mfaData.ext_user_id) {
                        mfaVerificationData = {
                            mfa_session_id: mfaData.mfa_session_id,
                            ext_user_id: mfaData.ext_user_id
                        };
                    }
                    // Check if it's nested under mfaVerifyData (from SetupMFAForm)
                    else if (mfaData.mfaVerifyData && mfaData.mfaVerifyData.mfa_session_id && mfaData.mfaVerifyData.ext_user_id) {
                        mfaVerificationData = {
                            mfa_session_id: mfaData.mfaVerifyData.mfa_session_id,
                            ext_user_id: mfaData.mfaVerifyData.ext_user_id
                        };
                    }
                    // Check if it's nested under mfa_data (from signup API)
                    else if (mfaData.mfa_data && mfaData.mfa_data.mfa_session_id && mfaData.mfa_data.ext_user_id) {
                        mfaVerificationData = {
                            mfa_session_id: mfaData.mfa_data.mfa_session_id,
                            ext_user_id: mfaData.mfa_data.ext_user_id
                        };
                    }
                    // Check if it's nested under verification_data
                    else if (mfaData.verification_data && mfaData.verification_data.mfa_session_id && mfaData.verification_data.ext_user_id) {
                        mfaVerificationData = {
                            mfa_session_id: mfaData.verification_data.mfa_session_id,
                            ext_user_id: mfaData.verification_data.ext_user_id
                        };
                    }
                }

                return {
                    formData: {
                        email: authFormData.email,
                        mfaVerificationData: mfaVerificationData
                    } as any,
                    onChangeAuthForm: onChangeAuthForm,
                    validatePasswordMatch,
                    validateNewPasswordMatch,
                    validateOTPMatch
                };
            default:
                return null;
        }
    }, [authFormData, mfaData, onChangeAuthForm, validatePasswordMatch, validateNewPasswordMatch, validateOTPMatch]);

    const returnData: UseAuthFlowManagerReturn = {
        // Core state
        currentStep,
        loginError,
        isLoading,
        mfaData,
        verificationSubmitted,
        passwordSubmitted,
        stepConfig,
        csrfToken,

        // Step state setters
        setStateLogin,
        setStateSignup,
        setStateEmail,
        setStateEmailVerify,
        setStatePasswordConfirmation,
        setStateNewPassword,
        setStateMFASetup,
        setStateMFAVerify,

        // Step configuration helpers
        isStepVisible,
        isStepEnabled,
        isStepDisabled,
        disableLastStep,

        // Flow control
        handleStepTransition,
        handleLoginSuccess,
        handleLoginError,

        // Forgot password handlers
        handleEmailSubmit,
        handleVerificationSubmit,
        handlePasswordSubmit,
        handleResendCode,

        // Signup handlers
        handleSignupInfoSubmit,
        handleSignupSubmit,

        // Confirm handlers
        handleConfirmEmailSubmit,

        // Reset functions
        resetToLogin,
        resetAllSteps,

        // Utility functions
        getCurrentStepConfig,
        updateStepConfig,
        getStepData,

        // Loading state control
        setIsLoading,

        // Form data access
        authFormData,
        onChangeAuthForm,
        resetAuthForm,
        handleLogin,
        setupMFAForm,
        verifyMFAForm
    };

    return returnData;
};
